import { ChevronRightOutlined } from '@mui/icons-material';
import { Slider, useTheme } from '@mui/material';
import { Box, Stack, Theme, Typography, alpha } from '@mui/material';
import { SatisfactionRateIcon } from '../../journey/utils/SatisfactionRateIcon';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { OodColorVariant } from '../../types/colors';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';
import getOodColor from '../../utils/getOodColor';

type Props = Readonly<{
	satisfaction: number;
	name: string;
}>;

export const SummarySatisfactionBlock: FC<Props> = ({ satisfaction, name }) => {
	const theme = useTheme();

	const satisfactionColors = getColors(theme, satisfaction);
	return (
		<Stack
			pt={1.5}
			pb={2}
			px={2}
			gap={1.5}
			border={'1px solid'}
			bgcolor={satisfactionColors.background}
			borderColor={satisfactionColors.border}
			borderRadius={1.5}
		>
			<Stack flexDirection={'row'} alignItems={'center'}>
				<Typography fontWeight={500} fontSize={14}>
					<FormattedMessage id={'journey.summary.drawer.satisfaction.title'} />
				</Typography>
				<ChevronRightOutlined fontSize={'small'} sx={{ color: (theme) => theme.palette[getNightOrDayPalette(theme)][500] }} />
				<Typography fontWeight={400} color={(theme) => theme.palette[getNightOrDayPalette(theme)][500]} fontSize={14}>
					{name}
				</Typography>
			</Stack>

			<Stack flexDirection={'row'} flexWrap={'nowrap'} alignItems={'center'} gap={1.5}>
				<Stack alignItems={'center'} position={'relative'} flex={1}>
					<Slider
						value={satisfaction}
						disabled
						max={100}
						sx={{
							'.MuiSlider-thumb': {
								display: 'none',
							},
							'.MuiSlider-rail': {
								height: 8,
								border: 'none',
								bgcolor: alpha(satisfactionColors.content as string, 0.75),
							},
							'.MuiSlider-track': {
								bgcolor: satisfactionColors.content,
								border: 'none',
								height: 8,
							},
						}}
					/>
					<SatisfactionThumb value={satisfaction} />
				</Stack>
				{/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
				<Typography>{satisfaction}%</Typography>
			</Stack>
		</Stack>
	);
};

type ThumbProps = Readonly<{ value: number }>;

const SatisfactionThumb: FC<ThumbProps> = ({ value }) => {
	return (
		<Box
			sx={{
				p: 1,
				lineHeight: '12px',
				borderRadius: '50%',
				boxShadow: (theme) =>
					theme.palette.mode === 'light'
						? '0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12)'
						: 'unset',
				position: 'absolute',
				zIndex: 1,
				bottom: -4,
				left: `calc(${value}% - 24px)`,
				pointerEvents: 'none',
				outline: (theme) => (theme.palette.mode === 'dark' ? `1px solid ${theme.palette.night[300]}` : 'unset'),
				background: (theme) => theme.palette[getNightOrDayPalette(theme)][0],
			}}
		>
			<SatisfactionRateIcon rate={value} />
		</Box>
	);
};

const getColors = (theme: Theme, value: number): OodColorVariant => {
	if (value >= 0 && value < 20) {
		return getOodColor(theme, 'red');
	} else if (value >= 20 && value < 40) {
		return getOodColor(theme, 'orange');
	} else if (value >= 40 && value < 60) {
		return getOodColor(theme, 'grey');
	} else if (value >= 60 && value < 80) {
		return getOodColor(theme, 'green');
	}
	return getOodColor(theme, 'darkGreen');
};
